import type { Banner } from '@setplex/tria-api'
import type { TFunction } from '~/core/i18n'
import type {
  AdjustPurchaseableFn,
  AdjustPurchaseableFnFactory,
} from '~/features/payment/sse-purchases'

import { MediaTypes, PurchaseInfoContentPurchaseType } from '@setplex/tria-api'
import { formatSeasonCountLabel } from '~/entities/shows/lib'
import { getLabelPrice } from '~/shared/tools/payment'

type BannerLabelFields = {
  year?: number | string | null
  length?: string | null
  ageRatings?: string | null
  seasonCount?: number | null
}

const toLabel = (value: number | string | null | undefined): string => {
  if (value == null || value === '') return ''
  return String(value)
}

const bannerLabelFields = (content: object): BannerLabelFields =>
  content as BannerLabelFields

/** Home/module banner metadata row: year, duration or seasons, age rating. */
export const getBannerContentLabels = ({
  contentType,
  content,
  t,
}: {
  contentType?: string | null
  content?: object | null
  t: TFunction
}): string[] => {
  if (!content) return []

  const { year, length, ageRatings, seasonCount } = bannerLabelFields(content)

  if (contentType === MediaTypes.VOD) {
    return [toLabel(year), length, ageRatings].filter(
      (value): value is string => Boolean(value)
    )
  }

  if (contentType === MediaTypes.TV_SHOW) {
    return [
      toLabel(year),
      formatSeasonCountLabel(seasonCount, t),
      ageRatings,
    ].filter((value): value is string => Boolean(value))
  }

  return []
}

/** Age rating for the PIN-locked VOD/TV Show banner description. */
export const getBannerAgeRatingLabel = ({
  contentType,
  content,
}: {
  contentType?: string | null
  content?: object | null
}): string | undefined => {
  if (!content) return undefined
  if (contentType !== MediaTypes.VOD && contentType !== MediaTypes.TV_SHOW) {
    return undefined
  }

  const label = toLabel(bannerLabelFields(content).ageRatings)
  return label || undefined
}

/** WBS badges above the banner title (New Movie, New Series, …). */
export const getBannerTopContentLabels = ({
  content,
  t,
}: {
  content?: object | null
  t: TFunction
}): string[] => {
  if (!content || !('labels' in content)) return []

  const { labels } = content as { labels?: string[] }
  return (labels ?? []).map((key) => t(key))
}

const formatBannerPrice = (
  banner: Banner,
  adjustFactory: AdjustPurchaseableFnFactory,
  t?: TFunction
): Banner => {
  if (banner.content) {
    const adjust: AdjustPurchaseableFn = (() => {
      switch (banner.contentType) {
        case MediaTypes.TV_CHANNEL:
          return adjustFactory('channel', 'channelSet')
        case MediaTypes.TV_SHOW:
          return adjustFactory('tvShow', 'tvShowSet')
        case MediaTypes.VOD:
          return adjustFactory('vod', 'vodSet')
        case MediaTypes.EPISODE:
          return adjustFactory('episode', 'tvShowSet')
        case MediaTypes.RADIO_CHANNEL:
          return adjustFactory('channel', 'channelSet')
        default:
          return (_) => _
      }
    })()

    return {
      ...banner,
      // issue due to ContentItem '~/shared/interfaces' != ContentItem tria-api/interfaces/banners
      content: withBannerLabels(
        withTopContentLabels(
          withBannerPriceFields(adjust(banner.content), banner.contentType),
          t
        ),
        banner.contentType,
        t
      ),
    }
  }
  return banner
}

const withTopContentLabels = <T extends object>(
  content: T,
  t?: TFunction
): T & { contentLabels?: string[] } => {
  const contentLabels = t ? getBannerTopContentLabels({ content, t }) : []
  if (!contentLabels.length) return content

  return { ...content, contentLabels }
}

type BannerPriceFields = {
  free?: boolean
  purchaseInfo?: {
    contentPurchaseType?: PurchaseInfoContentPurchaseType
  }
}

const hasBannerPriceFields = (
  content: object
): content is object & BannerPriceFields => 'free' in content

const withBannerPriceFields = <T extends object>(
  content: T,
  contentType: Banner['contentType']
): T => {
  switch (contentType) {
    case MediaTypes.VOD:
    case MediaTypes.TV_SHOW:
    case MediaTypes.TV_CHANNEL: {
      if (!hasBannerPriceFields(content)) return content
      const labelPrice = getLabelPrice({
        free: content.free,
        purchaseInfo: content.purchaseInfo,
      })
      const rented =
        content.purchaseInfo?.contentPurchaseType ===
        PurchaseInfoContentPurchaseType.Rented
      return { ...content, labelPrice, rented }
    }
    default:
      return content
  }
}

/** Precomputes the metadata row on banner content for skin-based carousels. */
const withBannerLabels = <T extends object>(
  content: T,
  contentType: Banner['contentType'],
  t?: TFunction
): T & { bannerLabels?: string[] } => {
  const bannerLabels = t
    ? getBannerContentLabels({ contentType, content, t })
    : []
  if (!bannerLabels.length) return content

  return { ...content, bannerLabels }
}

export const clearFieldsBanner =
  (
    adjustFactory: AdjustPurchaseableFnFactory = () => (_) => _,
    t?: TFunction
  ) =>
  (banner: Banner): Banner =>
    formatBannerPrice(banner, adjustFactory, t)
